import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { User, getAuth, signOut } from 'firebase/auth';
import { getFirebaseApp } from '../core/firebase';
import { orgIdFromUrl } from '../core/org-url';
import { AuthBridgeService } from '../core/services/auth-bridge.service';
import { OrgContextService } from '../core/services/org-context.service';

// Refresh the cached `emailVerified` flag from Firebase (e.g. user clicked the
// verification link in another tab) before deciding whether to bounce them.
//
// Microsoft's OIDC sign-in doesn't surface an `email_verified` claim the way
// Google does, so Firebase leaves `emailVerified=false` after a successful
// signInWithPopup with microsoft.com — even though Microsoft has fully
// authenticated the user and asserted their email. Treat a linked
// microsoft.com provider as equivalent verification.
async function isEmailVerified(user: User): Promise<boolean> {
  if (user.emailVerified) return true;
  if (user.providerData.some((p) => p.providerId === 'microsoft.com')) return true;
  try {
    await user.reload();
  } catch {
    // Don't lock the user out on a transient network failure.
    return true;
  }
  return user.emailVerified;
}

export const redirectUnauthorizedTo = (_redirect: string) => {
  return (() => {
    const guard: CanActivateFn = async (route, state) => {
      const bridge = inject(AuthBridgeService).bridge;
      const orgContext = inject(OrgContextService);
      const router = inject(Router);

      // Resolve a local session, or exchange an authorization-code callback.
      // Works the same on localhost (against staging auth) and deployed origins.
      let user: User | null;
      try {
        user = await bridge.ensureSession();
      } catch {
        user = null;
      }

      // No session → start the authorization-code flow at auth.crewdle.ai.
      if (!user) {
        await bridge.login();
        return false;
      }

      if (!(await isEmailVerified(user))) {
        await signOut(getAuth(getFirebaseApp())).catch(() => {});
        await bridge.login();
        return false;
      }

      // After a fresh code-exchange login the session is established here (late),
      // so OrgContext's bootstrap init() ran without a user. Load orgs now —
      // before the requireOrg child guard runs — so it doesn't briefly bounce
      // to /new while org data is still loading.
      // The org being opened comes from the URL (`/o/{orgId}`) when it names one.
      if (!orgContext.currentOrg()) {
        await orgContext.init(orgIdFromUrl(state.url)).catch(() => undefined);
      }

      // Strip the OAuth callback params from the URL. The bridge clears them via
      // history.replaceState, but Angular Router re-applies this navigation's
      // query params when it finalizes — so redirect to the clean path instead.
      if (route.queryParamMap.has('code')) {
        return router.parseUrl(state.url.split('?')[0]);
      }

      return true;
    };
    return guard;
  })();
};

// Used by /login and /signup: if already signed in, go to the app; otherwise
// hand off to centralized auth instead of rendering the in-app login page.
export const redirectLoggedInTo = (redirect: string) => {
  return (() => {
    const guard: CanActivateFn = async () => {
      const router = inject(Router);
      const bridge = inject(AuthBridgeService).bridge;

      let user: User | null;
      try {
        user = await bridge.ensureSession();
      } catch {
        user = null;
      }

      // Already signed in → into the app; otherwise hand off to the portal.
      // (Org invites are now handled entirely by the auth portal.)
      if (user) return router.parseUrl(redirect);

      await bridge.login();
      return false;
    };
    return guard;
  })();
};
